import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Wallet, Receipt } from 'lucide-react';
import { Card, CardContent } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { trpc } from '@/client/utils/trpc';
import { Sparkline } from './Sparkline';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTH_ABBR = MONTHS.map((m) => m.slice(0, 3));
const CATEGORY_COLORS = ['#6366f1', '#f59e0b', '#3b82f6', '#a3a3a3', '#737373', '#525252'];

function fmt(n: number) {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function deltaPct(current: number, prev: number): string | null {
  if (prev === 0) return null;
  const pct = ((current - prev) / prev) * 100;
  return `${pct >= 0 ? '+' : ''}${Math.round(pct)}%`;
}

function getGreeting(name?: string): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return `Good ${part}${name ? `, ${name.split(' ')[0]}` : ''}`;
}

function Skeleton({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-muted ${className ?? ''}`} />;
}

function shiftMonth(year: number, month: number, delta: number) {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function DashboardPage() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);

  const { data, isLoading } = trpc.finance.dashboardSummary.useQuery({ year, month });
  const { data: me } = trpc.user.me.useQuery();

  function go(delta: number) {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  }

  const monthLabel = `${MONTHS[month - 1]} ${year}`;
  const incomeDelta = data
    ? deltaPct(data.currentMonth.totalIncome, data.prevMonth.totalIncome)
    : null;
  const outcomeDelta = data
    ? deltaPct(data.currentMonth.totalOutcome, data.prevMonth.totalOutcome)
    : null;
  const sparkValues = data?.monthlyHistory.map((h) => h.income) ?? [];

  const incomeAhead = data != null && data.currentMonth.totalIncome >= data.prevMonth.totalIncome;
  const headlineHighlight = (() => {
    if (!data) return null;
    if (data.prevMonth.totalIncome === 0 && data.currentMonth.totalIncome === 0)
      return { prefix: 'No finance data recorded for this month.', highlight: '', suffix: '' };
    if (data.prevMonth.totalIncome === 0)
      return {
        prefix: 'First month with recorded income —',
        highlight: ' welcome to the ledger',
        suffix: '.',
      };
    const pct = `${incomeAhead ? '+' : ''}${Math.round(((data.currentMonth.totalIncome - data.prevMonth.totalIncome) / data.prevMonth.totalIncome) * 100)}%`;
    return {
      prefix: 'The studio is ',
      highlight: pct + (incomeAhead ? ' ahead of plan' : ' behind plan'),
      suffix: ' this month.',
    };
  })();

  // Category breakdown: top 5 + "Other"
  const categoryRows = (() => {
    if (!data || data.byCategory.length === 0) return [];
    if (data.byCategory.length <= 6)
      return data.byCategory.map((c, i) => ({ ...c, color: CATEGORY_COLORS[i] ?? '#525252' }));
    const top5 = data.byCategory
      .slice(0, 5)
      .map((c, i) => ({ ...c, color: CATEGORY_COLORS[i] ?? '#525252' }));
    const otherAmt = data.byCategory.slice(5).reduce((s, c) => s + c.amount, 0);
    return [...top5, { name: 'Other', amount: otherAmt, color: CATEGORY_COLORS[5] ?? '#525252' }];
  })();

  const totalOutcome = data?.currentMonth.totalOutcome ?? 0;

  return (
    <div className="mx-auto max-w-[1240px] space-y-7">
      {/* Period controls */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border px-1">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => go(-1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[130px] text-center text-sm font-medium">{monthLabel}</span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => go(1)}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-1 rounded-md border p-1">
          {(['Month', 'Quarter', 'Year'] as const).map((tab) => (
            <button
              type="button"
              key={tab}
              disabled={tab !== 'Month'}
              title={tab !== 'Month' ? 'Coming soon' : undefined}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                tab === 'Month'
                  ? 'bg-primary text-primary-foreground'
                  : 'cursor-not-allowed text-muted-foreground opacity-50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <Link to="/finance" search={{ type: 'ALL', month: '', category: '' }}>
            <Button variant="outline" size="sm">
              Open ledger
            </Button>
          </Link>
        </div>
      </div>

      {/* Editorial header */}
      <div className="flex items-end justify-between gap-6 border-b pb-6">
        <div className="flex flex-col gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {getGreeting(me?.full_name ?? undefined)} · {monthLabel} · Finance report
          </p>
          {isLoading ? (
            <>
              <Skeleton className="h-10 w-[460px]" />
              <Skeleton className="mt-1 h-4 w-64" />
            </>
          ) : headlineHighlight ? (
            <>
              <h1 className="max-w-[640px] text-[38px] font-semibold leading-[1.08] tracking-[-0.025em]">
                {headlineHighlight.prefix}
                {headlineHighlight.highlight && (
                  <span className={incomeAhead ? 'text-good' : 'text-bad'}>
                    {headlineHighlight.highlight}
                  </span>
                )}
                {headlineHighlight.suffix}
              </h1>
              {data?.recentActivity[0] && (
                <p className="text-sm text-muted-foreground">
                  {data.recentActivity[0].description}
                </p>
              )}
            </>
          ) : null}
        </div>

        <div className="flex shrink-0 gap-6">
          {isLoading ? (
            <>
              <Skeleton className="h-16 w-28" />
              <Skeleton className="h-16 w-28" />
            </>
          ) : data ? (
            <>
              <div className="min-w-[110px]">
                <p className="text-[11px] text-muted-foreground">Net</p>
                <p
                  className={`tnum mt-0.5 text-[26px] font-semibold tracking-[-0.02em] ${
                    data.currentMonth.balance >= 0 ? 'text-good' : 'text-bad'
                  }`}
                >
                  €{fmt(data.currentMonth.balance)}
                </p>
                {incomeDelta && (
                  <p
                    className={`mt-0.5 text-xs font-medium ${incomeAhead ? 'text-good' : 'text-bad'}`}
                  >
                    {incomeDelta}
                  </p>
                )}
              </div>
              <div className="min-w-[110px]">
                <p className="text-[11px] text-muted-foreground">Runway</p>
                <p className="tnum mt-0.5 text-[26px] font-semibold tracking-[-0.02em]">
                  {data.runway !== null ? `${Math.round(data.runway)} mo` : '—'}
                </p>
                {data.runway !== null && (
                  <p className="mt-0.5 text-xs text-muted-foreground">at current burn</p>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-4">
        {/* Income */}
        <Card>
          <CardContent className="pt-[22px]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Income · {MONTH_ABBR[month - 1]}</p>
                {isLoading ? (
                  <Skeleton className="mt-2 h-9 w-36" />
                ) : (
                  <p className="tnum mt-1.5 text-[34px] font-semibold tracking-[-0.02em]">
                    €{fmt(data?.currentMonth.totalIncome ?? 0)}
                  </p>
                )}
                {!isLoading && (
                  <div className="mt-2 flex items-center gap-2">
                    {incomeDelta && (
                      <Badge
                        variant={incomeAhead ? 'default' : 'destructive'}
                        className="text-[11px]"
                      >
                        {incomeDelta}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      vs €{fmt(data?.prevMonth.totalIncome ?? 0)} prev
                    </span>
                  </div>
                )}
              </div>
              {!isLoading && sparkValues.length >= 2 && (
                <Sparkline values={sparkValues} color="#22c55e" width={140} height={48} />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Outcome */}
        <Card>
          <CardContent className="pt-[22px]">
            <p className="text-xs text-muted-foreground">Outcome · {MONTH_ABBR[month - 1]}</p>
            {isLoading ? (
              <Skeleton className="mt-2 h-9 w-32" />
            ) : (
              <p className="tnum mt-1.5 text-[34px] font-semibold tracking-[-0.02em]">
                €{fmt(data?.currentMonth.totalOutcome ?? 0)}
              </p>
            )}
            {!isLoading && data && (
              <div className="mt-2 flex items-center gap-2">
                {outcomeDelta && (
                  <Badge
                    variant={
                      data.currentMonth.totalOutcome <= data.prevMonth.totalOutcome
                        ? 'default'
                        : 'destructive'
                    }
                    className="text-[11px]"
                  >
                    {outcomeDelta}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  vs €{fmt(data.prevMonth.totalOutcome)} prev
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bank balance */}
        <Card>
          <CardContent className="pt-[22px]">
            <p className="text-xs text-muted-foreground">Bank balance</p>
            {isLoading ? (
              <Skeleton className="mt-2 h-9 w-32" />
            ) : (
              <p className="tnum mt-1.5 text-[34px] font-semibold tracking-[-0.02em]">
                €{fmt(data?.cumulativeBalance ?? 0)}
              </p>
            )}
            {!isLoading && (
              <p className="mt-2 text-xs text-muted-foreground">Cumulative all-time balance</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Middle columns */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-4">
        {/* Where the money goes */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between border-b px-[22px] py-[18px]">
            <div>
              <p className="text-sm font-semibold">Where the money goes</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {monthLabel} · €{fmt(totalOutcome)} in outcomes
              </p>
            </div>
            <Link to="/finance" search={{ type: 'ALL', month: '', category: '' }}>
              <Button variant="ghost" size="sm">
                Details →
              </Button>
            </Link>
          </div>
          <div className="px-[22px] py-[14px]">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : categoryRows.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No outcome data for this month.
              </p>
            ) : (
              <div>
                {categoryRows.map((row) => {
                  const pct = totalOutcome > 0 ? (row.amount / totalOutcome) * 100 : 0;
                  return (
                    <div
                      key={row.name}
                      className="grid grid-cols-[140px_1fr_80px] items-center gap-[14px] py-2.5"
                    >
                      <div className="flex items-center gap-[9px]">
                        <span
                          className="h-2 w-2 shrink-0 rounded-sm"
                          style={{ background: row.color }}
                        />
                        <span className="text-sm font-medium">{row.name}</span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, background: row.color }}
                        />
                      </div>
                      <p className="tnum text-right text-sm font-semibold">€{fmt(row.amount)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* Upcoming */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between border-b px-[22px] py-[18px]">
            <p className="text-sm font-semibold">Upcoming</p>
            <Link to="/finance" search={{ type: 'ALL', month: '', category: '' }}>
              <Button variant="ghost" size="sm">
                All →
              </Button>
            </Link>
          </div>
          <div className="p-2">
            {isLoading ? (
              <div className="space-y-2 p-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : !data?.upcoming.length ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No scheduled entries.
              </p>
            ) : (
              data.upcoming.map((r) => (
                <div key={r.id} className="flex items-center gap-3 rounded-md px-[14px] py-[10px]">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    {r.type === 'INCOME' ? (
                      <Receipt className="h-4 w-4" />
                    ) : (
                      <Wallet className="h-4 w-4" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium">{r.description}</p>
                    <p className="text-[11px] text-muted-foreground">{r.date}</p>
                  </div>
                  <p
                    className={`tnum text-[13px] font-semibold ${
                      r.type === 'INCOME' ? 'text-good' : ''
                    }`}
                  >
                    {r.type === 'INCOME' ? '+' : '–'}€{fmt(r.amount)}
                  </p>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Recent activity */}
      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between border-b px-[22px] py-[18px]">
          <div>
            <p className="text-sm font-semibold">Recent activity</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Latest entries across income and outcome
            </p>
          </div>
          <Link to="/finance" search={{ type: 'ALL', month: '', category: '' }}>
            <Button variant="outline" size="sm">
              Open ledger →
            </Button>
          </Link>
        </div>
        {isLoading ? (
          <div className="space-y-3 p-[22px]">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        ) : !data?.recentActivity.length ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No entries yet.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="px-[22px] py-3 text-left text-xs font-medium text-muted-foreground">
                  Date
                </th>
                <th className="px-[22px] py-3 text-left text-xs font-medium text-muted-foreground">
                  Type
                </th>
                <th className="px-[22px] py-3 text-left text-xs font-medium text-muted-foreground">
                  Category
                </th>
                <th className="px-[22px] py-3 text-left text-xs font-medium text-muted-foreground">
                  Description
                </th>
                <th className="px-[22px] py-3 text-right text-xs font-medium text-muted-foreground">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {data.recentActivity.map((r, i) => (
                <tr key={r.id} className={i < data.recentActivity.length - 1 ? 'border-b' : ''}>
                  <td className="tnum px-[22px] py-3 text-sm">{r.date}</td>
                  <td className="px-[22px] py-3">
                    {r.type === 'INCOME' ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-good-bg px-2 py-0.5 text-[12px] font-medium text-good">
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        Income
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[12px] font-medium">
                        Outcome
                      </span>
                    )}
                  </td>
                  <td className="px-[22px] py-3 text-sm">{r.category}</td>
                  <td className="px-[22px] py-3 text-sm text-muted-foreground">
                    <div className="max-w-[220px] truncate">{r.description}</div>
                  </td>
                  <td
                    className={`tnum px-[22px] py-3 text-right text-sm font-semibold ${
                      r.type === 'INCOME' ? 'text-good' : ''
                    }`}
                  >
                    {r.type === 'INCOME' ? '+' : '–'}€{fmt(r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
